import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import CarteraStats from "@/pages/admin/cartera/components/CarteraStats";
import AdvisorCarteraPanel from "@/pages/admin/cartera/components/AdvisorCarteraPanel";
import ReceivablesPanel from "@/pages/admin/cartera/components/ReceivablesPanel";
import ReceivableFormModal from "@/pages/admin/cartera/components/ReceivableFormModal";
import PaymentModal from "@/pages/admin/cartera/components/PaymentModal";
import LoansPanel from "@/pages/admin/cartera/components/LoansPanel";
import type { Loan, Receivable } from "@/types";
import { receivableBalance } from "@/utils/accounting";

type CarteraFilter = "all" | "pending" | "settled";

const CarteraPage = () => {
  const { data, addReceivable, updateReceivable, removeReceivable, addLoan, removeLoan } = useApp();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<CarteraFilter>("all");
  const [newOpen, setNewOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<Receivable | null>(null);
  const [feedback, setFeedback] = useState("");

  const pendingCount = useMemo(
    () => data.receivables.filter((r) => receivableBalance(r) > 0).length,
    [data.receivables]
  );
  const settledCount = data.receivables.length - pendingCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.receivables.filter((r) => {
      const balance = receivableBalance(r);
      if (status === "pending" && balance <= 0) return false;
      if (status === "settled" && balance > 0) return false;
      if (!q) return true;
      return (
        r.clientName.toLowerCase().includes(q) || r.productName.toLowerCase().includes(q)
      );
    });
  }, [data.receivables, query, status]);

  const filterTabs: { key: CarteraFilter; label: string; count: number }[] = [
    { key: "all", label: "Todas", count: data.receivables.length },
    { key: "pending", label: "Pendientes", count: pendingCount },
    { key: "settled", label: "Pagadas", count: settledCount },
  ];

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2400);
  };

  const handleCreate = async (r: Receivable) => {
    await addReceivable(r);
    setNewOpen(false);
    notify("Fiado registrado en la cartera.");
  };

  const handlePayment = async (r: Receivable) => {
    await updateReceivable(r);
    setPayTarget(null);
    notify("Abono aplicado correctamente.");
  };

  const handleDeleteReceivable = async (id: string) => {
    await removeReceivable(id);
    notify("Cuenta eliminada.");
  };

  const handleAddLoan = async (l: Loan) => {
    await addLoan(l);
    notify("Movimiento de préstamo registrado.");
  };

  const handleDeleteLoan = async (id: string) => {
    await removeLoan(id);
    notify("Movimiento eliminado.");
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Cartera y Préstamos"
        subtitle="Saldos por cobrar: deuda de asesores (catálogo menos su descuento), fiados a clientes con abonos y saldo autocalculado, más la cuenta de préstamos inter-empresa Juank.unlimited."
      />

      <CarteraStats receivables={data.receivables} />

      <div className="mt-5">
        <AdvisorCarteraPanel data={data} />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mt-5 mb-4">
        <div className="inline-flex items-center gap-1 px-1 py-1 rounded-full bg-background-200/50 border border-background-300/40 self-start">
          {filterTabs.map((t) => {
            const active = status === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                aria-pressed={active}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? "bg-accent-500 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900 hover:bg-background-300/40"
                }`}
              >
                {t.key === "pending" && <i className="ri-hourglass-line" />}
                {t.label}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    active ? "bg-foreground-950/15" : "bg-background-300/50"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 max-w-md">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-search-line" />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por cliente o producto..."
            className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
            aria-label="Buscar cuenta por cobrar"
          />
        </div>
        <button
          onClick={() => setNewOpen(true)}
          className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 lg:ml-auto"
        >
          <i className="ri-add-line" /> Nuevo fiado
        </button>
      </div>

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      <ReceivablesPanel
        receivables={filtered}
        onPay={setPayTarget}
        onDelete={handleDeleteReceivable}
      />

      <div className="mt-5">
        <LoansPanel loans={data.loans} onAdd={handleAddLoan} onDelete={handleDeleteLoan} />
      </div>

      <ReceivableFormModal
        open={newOpen}
        clients={data.clients}
        products={data.products}
        inventory={data.inventory}
        onClose={() => setNewOpen(false)}
        onSubmit={handleCreate}
      />
      <PaymentModal
        open={payTarget !== null}
        receivable={payTarget}
        onClose={() => setPayTarget(null)}
        onSubmit={handlePayment}
      />
    </div>
  );
};

export default CarteraPage;